import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MessageCircle, Calendar, CheckCircle2, Star, Shield, Zap, Heart, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import Footer from "../components/Footer";

const STEPS = [
  { icon: Search, title: "Browse verified workers", text: "Search by suburb, support type, language, gender preference, availability and rate. Read profiles, reviews, and watch intro videos.", color: "bg-sky-100 text-sky-600" },
  { icon: MessageCircle, title: "Message before booking", text: "Free in-app chat with any worker. Ask questions, share your needs, and find someone you click with — no cost, no commitment.", color: "bg-emerald-100 text-emerald-600" },
  { icon: Calendar, title: "Book a shift or post one", text: "Lock in a one-off, recurring, or open shift. Workers in your area are notified instantly.", color: "bg-amber-100 text-amber-600" },
  { icon: CheckCircle2, title: "Get supported, your way", text: "After each shift, the worker submits a timesheet — you approve, we generate the NDIS-aligned invoice. Done.", color: "bg-violet-100 text-violet-600" }
];

const PERKS = [
  { icon: Zap, title: "Zero participant fees", text: "What you see is what you pay. We charge workers a small platform fee — never you." },
  { icon: Shield, title: "NDIS-screened workers", text: "Every worker goes through NDIS Worker Screening and document verification before going live." },
  { icon: Heart, title: "Plan management built-in", text: "Self-Managed, Plan-Managed, or NDIA-Agency-Managed — invoices route automatically." },
  { icon: Star, title: "You stay in control", text: "Choose your worker, set your hours, message anytime. Cancel up to 24h ahead with no fee." }
];

export default function HowItWorksParticipant() {
  return (
    <div className="min-h-screen bg-white">
      <header className="container mx-auto px-4 py-5 flex items-center justify-between max-w-6xl">
        <Link to="/" className="font-bold text-xl text-slate-900">BTR Health</Link>
        <div className="flex gap-3">
          <Link to="/how-it-works/workers" className="text-sm text-slate-600 hover:text-slate-900 hidden sm:block self-center">For workers</Link>
          <Link to="/"><Button variant="outline" className="rounded-full">Home</Button></Link>
          <Link to="/register"><Button className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full">Get started</Button></Link>
        </div>
      </header>

      <section className="container mx-auto px-4 py-16 max-w-4xl text-center">
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
          Find the right support<br /><span className="text-[#87CEEB]">in four simple steps</span>
        </motion.h1>
        <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Choose your own NDIS support workers, on your terms — without paying a cent in platform fees.
        </p>
      </section>

      <section className="container mx-auto px-4 max-w-5xl mb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl p-7 border border-slate-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 rounded-xl ${s.color} flex items-center justify-center`}>
                  <s.icon className="w-6 h-6" />
                </div>
                <span className="text-sm font-semibold text-slate-400">STEP {i + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">{s.title}</h3>
              <p className="text-slate-600 leading-relaxed">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-12">Why participants choose BTR Health</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PERKS.map(p => (
              <div key={p.title} className="bg-white rounded-2xl p-6 border border-slate-100">
                <div className="w-11 h-11 rounded-xl bg-[#87CEEB]/10 flex items-center justify-center mb-3">
                  <p.icon className="w-5 h-5 text-[#87CEEB]" />
                </div>
                <h4 className="font-bold text-slate-900 mb-1">{p.title}</h4>
                <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-20 max-w-3xl text-center">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Ready to find your support?</h2>
        <p className="text-slate-600 mb-8 text-lg">Join thousands of NDIS participants choosing their own workers on BTR Health.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/register"><Button size="lg" className="bg-[#87CEEB] hover:bg-[#6BB8DB] rounded-full px-8 h-12" data-testid="hiw-participant-register">Create free account<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
          <Link to="/search"><Button size="lg" variant="outline" className="rounded-full px-8 h-12">Browse workers</Button></Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
